import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Clock, MapPin, Route, Shirt, Award, Trophy, Users, Camera, Sparkles,
  Droplet, Backpack, Music, Plane, ShieldAlert, Eye, Footprints,
  CheckCircle2, AlertTriangle, Mail, Phone, Send, ExternalLink, Heart, Mountain,
  Star, Newspaper, Medal, Flame, Calendar
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Countdown from "@/components/Countdown";
import Logo from "@/components/Logo";
import { HOME } from "@/constants/testIds";
import { IMAGES } from "@/lib/images";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ id, eyebrow, title, intro, dark = false, children, testId }) => (
  <section
    id={id}
    data-testid={testId}
    className={`relative py-20 md:py-28 px-5 sm:px-8 ${dark ? "bg-[#0E1410] text-[#F7F1E8]" : "bg-[#F7F1E8] text-[#15140F]"}`}
  >
    <div className="max-w-7xl mx-auto">
      <div className="mb-12 md:mb-16 max-w-3xl">
        {eyebrow && (
          <div className="flex items-center gap-3 mb-5">
            <span className="divider-tick" />
            <span className="font-display uppercase tracking-[0.28em] text-xs text-[#E94E1B]">
              {eyebrow}
            </span>
          </div>
        )}
        <h2 className="font-black text-4xl md:text-6xl leading-[1.05] uppercase">
          {title}
        </h2>
        {intro && (
          <p className={`mt-5 text-lg ${dark ? "text-[#F7F1E8]/70" : "text-[#6B6358]"}`}>
            {intro}
          </p>
        )}
      </div>
      {children}
    </div>
  </section>
);

const Hero = () => (
  <section id="hero" data-testid={HOME.heroSection} className="relative min-h-screen flex items-end overflow-hidden">
    <div className="absolute inset-0">
      <img src={IMAGES.hero} alt="Sortida Cross de Carme" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-[#0E1410]" />
      <div className="absolute inset-0 grain" />
    </div>

    {/* Diagonal stripe accent */}
    <div className="absolute top-0 right-0 w-24 md:w-40 h-full stripes opacity-25 z-[1]" />

    <div className="relative z-10 max-w-7xl mx-auto w-full px-5 sm:px-8 pb-16 pt-32">
      <div className="reveal max-w-4xl">
        <div className="flex items-center gap-4 mb-6">
          <span className="stamp text-[#F2B23A]">XIII Edició</span>
          <span className="hidden sm:flex items-center gap-2 text-white/80 text-sm font-display uppercase tracking-widest">
            <span className="pulse-dot w-2 h-2 bg-[#E94E1B] rounded-full" />
            Cursa disputada · Resultats disponibles
          </span>
        </div>
        <h1 className="font-black uppercase text-white text-[2.5rem] sm:text-[4rem] md:text-[6rem] leading-[0.92] tracking-tight">
          <Logo size="hero" onDark />
        </h1>
        <p className="mt-7 text-lg md:text-xl text-white/85 max-w-2xl leading-relaxed">
          El passat <strong>4 de juliol</strong> es va celebrar a Carme una nova edició
          del Cross de Carme i la Caminada Popular «Carme ets Tu». Organitza el
          <strong> Training Club Esportiu Orpí — Carme ets Tu</strong>, amb el suport de
          l'<strong>Ajuntament de Carme</strong>.
        </p>

        {/* Stats row */}
        <div className="mt-9 grid grid-cols-2 sm:grid-cols-4 gap-px bg-white/10 border border-white/15">
          {[
            { k: "Guanyador", v: "X. TOMASA" },
            { k: "Rècord km", v: "2003 superat" },
            { k: "Lloc", v: "Carme" },
            { k: "Cursa", v: "5,7 km" },
          ].map((s) => (
            <div key={s.k} className="bg-[#0E1410]/60 backdrop-blur-md p-4 md:p-5">
              <div className="font-display uppercase tracking-[0.2em] text-[10px] text-[#F2B23A]">{s.k}</div>
              <div className="font-black text-xl md:text-2xl text-white mt-1">{s.v}</div>
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-4">
          <a
            data-testid={HOME.heroCtaInscribe}
            href="#resultats"
            className="group px-7 py-4 bg-[#E94E1B] text-white font-display uppercase tracking-wider text-sm inline-flex items-center gap-3 hover:bg-[#C03A12] transition-all"
          >
            Veure resultats
            <ExternalLink size={16} className="group-hover:translate-x-1 transition-transform" />
          </a>
          <a
            data-testid={HOME.heroCtaInfo}
            href="#info"
            className="px-7 py-4 border border-white/40 text-white font-display uppercase tracking-wider text-sm hover:bg-white hover:text-[#0E1410] transition-colors"
          >
            Més informació
          </a>
        </div>
      </div>
    </div>
  </section>
);

const AtletesXIV = () => {
  const masculi = [
    "Pau Nacenta", "Oriol Marín", "Xavi Tomasa", "Jan Isla", "Marc Espinar",
    "Bernat Rueda", "Quim Vich", "Uri Ramírez", "Ayrton Fontecha", "Ferran Comas",
    "Leo Contreras", "Sergio Domínguez", "Jordi Cifuentes", "Carlos Gámez",
    "Riduan Boulbayem", "Alberto Lao", "Adri Martín", "David Busquets", "Juanjo García",
  ];
  const femeni = [
    "Anna Noguera Raja", "Jessica Ruiz Izquierdo", "Maite Ríos Campoy",
    "Jordina Martínez Tosas", "Míriam López Cuerva", "Lídia Ramos Palacios",
    "Mamen del Castillo", "Kitina Silva", "Laura Nutrirun",
    "María Gijón Moreno", "Sonia Iron Will Pro", "Laura Madueño",
  ];

  return (
    <section
      id="atletes-xiv"
      data-testid="atletes-xiv-section"
      className="relative bg-[#F7F1E8] text-[#15140F] py-20 md:py-24 px-5 sm:px-8 border-b-4 border-[#0E1410] overflow-hidden"
    >
      <div className="absolute -top-16 -right-16 w-72 h-72 stripes opacity-[0.08] rotate-12 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        <div className="mb-10 max-w-3xl">
          <div className="flex items-center gap-3 mb-5">
            <span className="divider-tick" />
            <span className="font-display uppercase tracking-[0.28em] text-xs text-[#E94E1B]">
              XIV Cross de Carme · Preselecció
            </span>
          </div>
          <h2 className="font-black uppercase text-4xl md:text-6xl leading-[1.02]">
            Atletes seleccionats
          </h2>
          <div className="mt-4 inline-flex items-center gap-2 bg-[#F2B23A] text-[#0E1410] px-3 py-1.5 font-display uppercase tracking-widest text-[10px]">
            <AlertTriangle size={12} /> Pendents de confirmació
          </div>
          <p className="mt-4 text-base md:text-lg text-[#6B6358] max-w-3xl">
            Llistat provisional d'atletes seleccionats per a la XIV edició del Cross de Carme.
            Anirem confirmant els noms a mesura que es tanquin les participacions.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Masculí */}
          <div className="bg-[#0E1410] text-[#F7F1E8] p-6 md:p-7 border-l-4 border-[#E94E1B]">
            <div className="flex items-center gap-3 mb-5">
              <Flame size={20} className="text-[#E94E1B]" />
              <div>
                <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A]">
                  Categoria Masculina
                </div>
                <div className="font-black text-xl mt-0.5 tabular-nums">{masculi.length} atletes</div>
              </div>
            </div>
            <ol className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {masculi.map((n, i) => (
                <li key={n} className="flex items-center gap-3 py-1.5 border-b border-white/10">
                  <span className="w-7 h-7 flex items-center justify-center bg-[#E94E1B] text-white font-black text-xs tabular-nums shrink-0">
                    {i + 1}
                  </span>
                  <span className="font-display uppercase tracking-wide text-sm">{n}</span>
                </li>
              ))}
            </ol>
          </div>

          {/* Femení */}
          <div className="bg-[#0E1410] text-[#F7F1E8] p-6 md:p-7 border-l-4 border-[#F2B23A]">
            <div className="flex items-center gap-3 mb-5">
              <Flame size={20} className="text-[#F2B23A]" />
              <div>
                <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A]">
                  Categoria Femenina
                </div>
                <div className="font-black text-xl mt-0.5 tabular-nums">{femeni.length} atletes</div>
              </div>
            </div>
            <ol className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {femeni.map((n, i) => (
                <li key={n} className="flex items-center gap-3 py-1.5 border-b border-white/10">
                  <span className="w-7 h-7 flex items-center justify-center bg-[#F2B23A] text-[#0E1410] font-black text-xs tabular-nums shrink-0">
                    {i + 1}
                  </span>
                  <span className="font-display uppercase tracking-wide text-sm">{n}</span>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className="mt-8 bg-white border border-[#15140F]/10 p-5 flex flex-col md:flex-row items-start md:items-center gap-3">
          <AlertTriangle size={20} className="text-[#E94E1B] shrink-0 mt-0.5" />
          <p className="text-sm text-[#6B6358] leading-relaxed">
            <strong className="text-[#15140F]">Nota:</strong> Aquest llistat és una preselecció
            provisional. Les confirmacions definitives i les incorporacions es faran públiques
            gradualment. Estigueu atents a les novetats.
          </p>
        </div>
      </div>
    </section>
  );
};

const Resultats = () => {
  const top10M = [
    { pos: 1, name: "Xavi Tomasa", time: "19:11", note: "Supera el ritme al km d'Albert Ferrer del 2003", champion: true },
    { pos: 2, name: "Jan Isla", time: "19:37", note: "Iguala el ritme de l'Albert Ferrer" },
    { pos: 3, name: "Marc Espinar", time: "19:50" },
    { pos: 4, name: "Oriol Marín", time: "20:01", note: "Medalla de xocolata" },
    { pos: 5, name: "Ferran Comas", time: "20:33" },
    { pos: 6, name: "Pau Nacenta", time: "20:39" },
    { pos: 7, name: "Uri Ramírez", time: "20:57" },
    { pos: 8, name: "Ayrton Fontecha", time: "21:01" },
    { pos: 9, name: "Quim Vich", time: "21:07" },
    { pos: 10, name: "Bernat Rueda", time: "21:28" },
  ];
  const top10F = [
    { pos: 1, name: "Anna Noguera", time: "24:10", note: "Guanya al sprint a Jessica Ruiz", champion: true },
    { pos: 2, name: "Jessica Ruiz", time: "24:13", note: "Duel de sprint fins la meta" },
    { pos: 3, name: "Maite Ríos", time: "24:50" },
    { pos: 4, name: "Jordina Martínez", time: "24:59", note: "Medalla de xocolata" },
    { pos: 5, name: "Míriam López", time: "25:31" },
    { pos: 6, name: "Lídia Ramos", time: "25:59" },
    { pos: 7, name: "Júlia Subirana", time: "26:10" },
    { pos: 8, name: "Marta Sánchez", time: "26:46" },
    { pos: 9, name: "M. Teresa Segués", time: "27:27" },
    { pos: 10, name: "Montse Estany", time: "27:34" },
  ];

  const renderTop10 = (rows, labelHex = "#E94E1B") => (
    <ul className="divide-y divide-[#15140F]/10">
      {rows.map((r) => (
        <li
          key={r.pos}
          className={`flex items-center gap-4 px-5 py-3 ${r.champion ? "bg-[#E94E1B]/10" : ""}`}
        >
          <div
            className={`w-9 h-9 flex items-center justify-center font-black text-lg shrink-0 ${
              r.pos === 1
                ? "bg-[#E94E1B] text-white"
                : r.pos === 2
                ? "bg-[#0E1410] text-[#F2B23A]"
                : r.pos === 3
                ? "bg-[#F2B23A] text-[#0E1410]"
                : "bg-[#15140F]/10 text-[#15140F]"
            }`}
          >
            {r.pos}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold uppercase text-base">{r.name}</div>
            {r.note && (
              <div className="text-xs text-[#6B6358] mt-0.5">{r.note}</div>
            )}
          </div>
          {r.time && (
            <div className="font-display font-bold tabular-nums text-base md:text-lg text-[#0E1410] shrink-0">
              {r.time}
            </div>
          )}
        </li>
      ))}
    </ul>
  );

  const renderPodi = (rows, title, extraContent, podiumPhoto) => (
    <div className="bg-[#15140F] border-l-4 border-[#E94E1B] p-6 md:p-8">
      <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A] mb-4">
        {title}
      </div>
      {podiumPhoto && (
        <img
          src={podiumPhoto}
          alt={title}
          loading="lazy"
          className="w-full h-auto object-contain mb-6 border-2 border-[#F2B23A] bg-[#0E1410]"
        />
      )}
      <div className="grid grid-cols-3 gap-3 items-end">
        {/* 2nd */}
        <div className="text-center">
          <div className="font-black text-6xl md:text-7xl text-[#F7F1E8]/40">2</div>
          <div className="mt-2 bg-[#F7F1E8]/10 py-4 px-2">
            <div className="font-display font-bold uppercase text-xs md:text-sm leading-tight break-words hyphens-auto">{rows[1].name}</div>
          </div>
        </div>
        {/* 1st */}
        <div className="text-center">
          <div className="font-black text-7xl md:text-8xl text-[#F2B23A]">1</div>
          <div className="mt-2 bg-[#E94E1B] py-5 px-2 border-2 border-[#F2B23A]">
            <Trophy size={18} className="text-white mx-auto mb-1" />
            <div className="font-black uppercase text-sm md:text-base leading-tight text-white break-words hyphens-auto">{rows[0].name}</div>
          </div>
        </div>
        {/* 3rd */}
        <div className="text-center">
          <div className="font-black text-6xl md:text-7xl text-[#F7F1E8]/40">3</div>
          <div className="mt-2 bg-[#F7F1E8]/10 py-4 px-2">
            <div className="font-display font-bold uppercase text-xs md:text-sm leading-tight break-words hyphens-auto">{rows[2].name}</div>
          </div>
        </div>
      </div>
      {extraContent}
    </div>
  );

  return (
    <section
      id="resultats"
      data-testid="resultats-section"
      className="relative bg-[#0E1410] text-[#F7F1E8] py-20 md:py-28 px-5 sm:px-8 overflow-hidden"
    >
      {/* Diagonal stripes accent */}
      <div className="absolute top-0 right-0 w-40 md:w-64 h-full stripes opacity-20 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        {/* Photo strip · sortida oficial */}
        <div className="grid md:grid-cols-2 gap-4 mb-10">
          <img
            src="https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/u39thwxh_e9f56684-feeb-4954-972d-5bbc8773b503.jpg"
            alt="Sortida del XIII Cross de Carme sota l'arc Aneto"
            loading="lazy"
            className="w-full h-64 md:h-80 object-cover border-2 border-[#F2B23A]"
          />
          <img
            src="https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/tpsm0tnc_1fefc9e7-a52d-47b5-be25-5785c77b151c.jpg"
            alt="Corredors preparats a la sortida"
            loading="lazy"
            className="w-full h-64 md:h-80 object-cover border-2 border-[#F2B23A]"
          />
        </div>

        {/* Header */}
        <div className="border-b-4 border-[#E94E1B] pb-4 mb-10 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Trophy size={22} className="text-[#F2B23A]" />
            <span className="font-display uppercase tracking-[0.3em] text-xs text-[#F2B23A]">
              Resultats · XIII Cross de Carme
            </span>
          </div>
          <div className="flex items-center gap-2 font-display uppercase tracking-widest text-[10px] text-[#F7F1E8]/60">
            <span className="pulse-dot w-2 h-2 bg-[#E94E1B] rounded-full" />
            Directe · Minut a minut
          </div>
        </div>

        <div className="mb-10 max-w-4xl">
          <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A] mb-3">
            Guanyador
          </div>
          <h2 className="font-black uppercase text-4xl md:text-6xl leading-[0.98]">
            <span className="text-[#E94E1B]">Xavi Tomasa</span> guanya<br />
            el XIII Cross de Carme
          </h2>
          <p className="mt-5 text-lg text-[#F7F1E8]/80 max-w-3xl leading-relaxed">
            <strong>Xavi Tomasa</strong> supera el ritme al km d'<strong>Albert Ferrer</strong> del <strong>2003</strong>:
            el rècord estava a <strong>3:25/km</strong> i Xavi ho aconsegueix a <strong>3:20,8/km</strong>. Jan Isla
            i Marc Espinar completen el podi. <strong>Jan Isla iguala</strong> el ritme
            de l'Albert Ferrer.
          </p>
        </div>

        {/* Podi + top 10 · Masculí */}
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-6">
          {renderPodi(top10M, "Podi masculí", (
            <div className="mt-8 flex items-start gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/ucwvb2o6_IMG_5003.jpeg"
                alt="Xavi Tomasa creuant la meta"
                loading="lazy"
                className="w-24 h-32 sm:w-28 sm:h-36 object-cover border-2 border-[#F2B23A] shrink-0"
                style={{ objectPosition: "center top" }}
              />
              <div className="grid grid-cols-1 gap-3 flex-1">
                <div className="border-l-2 border-[#F2B23A] pl-4">
                  <div className="font-display uppercase tracking-widest text-[10px] text-[#F2B23A]">Rècord km</div>
                  <div className="font-black text-base mt-1">Supera A. Ferrer 2003</div>
                </div>
                <div className="border-l-2 border-[#F2B23A] pl-4">
                  <div className="font-display uppercase tracking-widest text-[10px] text-[#F2B23A]">Isla iguala</div>
                  <div className="font-black text-base mt-1">Ritme Albert Ferrer</div>
                </div>
              </div>
            </div>
          ), "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/i5gexhuq_IMG_5007.jpeg")}

          {/* Top 10 M table */}
          <div className="bg-white text-[#15140F] p-2">
            <div className="bg-[#0E1410] text-[#F7F1E8] px-5 py-3 font-display uppercase tracking-widest text-sm">
              Top 10 · Masculí
            </div>
            {renderTop10(top10M)}
          </div>
        </div>

        {/* Podi + top 10 · Femení */}
        <div className="mt-10">
          <div className="mb-6 max-w-4xl">
            <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A] mb-3">
              Guanyadora
            </div>
            <h3 className="font-black uppercase text-3xl md:text-5xl leading-[1.02]">
              <span className="text-[#E94E1B]">Anna Noguera</span> guanya al sprint
            </h3>
            <p className="mt-4 text-base md:text-lg text-[#F7F1E8]/80 max-w-3xl leading-relaxed">
              <strong>Anna Noguera</strong> s'imposa a <strong>Jessica Ruiz</strong> en un vibrant
              esprint final, i <strong>Maite Ríos</strong> completa el podi femení.
              L'Anna <strong>supera per la mínima el ritme que portava Carme Tort</strong>.
            </p>
          </div>

          <div className="grid lg:grid-cols-[1.15fr_1fr] gap-6">
            {renderPodi(top10F, "Podi femení", (
              <div className="mt-8 space-y-4">
                <div className="flex items-start gap-4">
                  <img
                    src="https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/ksi7z18i_IMG_5005.jpeg"
                    alt="Anna Noguera creuant la meta"
                    loading="lazy"
                    className="w-24 h-32 sm:w-28 sm:h-36 object-cover border-2 border-[#F2B23A] shrink-0"
                    style={{ objectPosition: "center top" }}
                  />
                  <div className="border-l-2 border-[#F2B23A] pl-4">
                    <div className="font-display uppercase tracking-widest text-[10px] text-[#F2B23A]">Ritme km · Anna Noguera</div>
                    <div className="font-black text-sm mt-1">Supera per la mínima el ritme de Carme Tort</div>
                  </div>
                </div>
              </div>
            ), "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/7gay1bcd_IMG_5006.jpeg")}
            <div className="bg-white text-[#15140F] p-2">
              <div className="bg-[#F2B23A] text-[#0E1410] px-5 py-3 font-display uppercase tracking-widest text-sm">
                Top 10 · Femení
              </div>
              {renderTop10(top10F)}
            </div>
          </div>
        </div>

        {/* Locals · Rècords */}
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          <div className="bg-[#E94E1B] text-white p-6 md:p-7 border-l-4 border-[#F2B23A]">
            <div className="flex items-center gap-2 font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A]">
              <Trophy size={14} /> Primera local femenina · Rècord històric
            </div>
            <div className="mt-3 font-black text-3xl md:text-4xl uppercase leading-tight">
              Berta Gabarró
            </div>
            <p className="mt-2 text-sm md:text-base text-white/90 leading-relaxed">
              Primera local femenina i <strong>bat el rècord històric del Cross de Carme</strong>.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 bg-[#0E1410] text-[#F2B23A] px-4 py-2 font-display font-bold text-2xl tabular-nums">
              29:41
            </div>
          </div>

          <div className="bg-[#15140F] text-[#F7F1E8] p-6 md:p-7 border-l-4 border-[#F2B23A]">
            <div className="flex items-center gap-2 font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A]">
              <Trophy size={14} /> Primer local masculí
            </div>
            <div className="mt-3 font-black text-3xl md:text-4xl uppercase leading-tight">
              Domènec Sellarès
            </div>
            <p className="mt-2 text-sm md:text-base text-[#F7F1E8]/85 leading-relaxed">
              Primer local masculí i <strong>6è millor ritme al km de la història</strong>{" "}
              dels temps locals del Cross de Carme.
            </p>
            <div className="mt-4 inline-flex items-center gap-2 bg-[#E94E1B] text-white px-4 py-2 font-display font-bold text-2xl tabular-nums">
              24:14
            </div>
          </div>
        </div>

        {/* Premis per categories */}
        <div className="mt-10">
          <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A] mb-4">
            Premis per categories
          </div>
          <h3 className="font-black uppercase text-3xl md:text-4xl leading-tight mb-6">
            Guanyadors de cada categoria
          </h3>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { cat: "Júnior femení", name: "Zoe Fortunati", time: "33:40" },
              { cat: "Júnior masculí", name: "Bernat Rueda", time: "21:28" },
              { cat: "Sènior femení", name: "Jordina Martínez", time: "24:59" },
              { cat: "Sènior masculí", name: "Oriol Marín", time: "20:01" },
              { cat: "+40 femení", name: "Susana Matarín", time: "27:39" },
              { cat: "+40 masculí", name: "Carlos Gámez", time: "22:27" },
              { cat: "+50 femení", name: "M. Teresa Segués", time: "27:27" },
              { cat: "+50 masculí", name: "David Busquets", time: "23:42" },
            ].map((p) => (
              <div key={p.cat} className="bg-[#15140F] p-5 border-l-4 border-[#F2B23A]">
                <div className="flex items-center gap-2 font-display uppercase tracking-widest text-[10px] text-[#F2B23A]">
                  <Trophy size={12} /> {p.cat}
                </div>
                <div className="font-black text-lg md:text-xl uppercase mt-2 leading-tight">
                  {p.name}
                </div>
                <div className="mt-2 inline-flex items-center bg-[#E94E1B] text-white px-3 py-1 font-display font-bold text-base tabular-nums">
                  {p.time}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* News · New circuit */}
        <div className="mt-10 bg-[#F2B23A] text-[#0E1410] p-6 md:p-8 grid md:grid-cols-[auto_1fr] gap-5 items-start border-l-4 border-[#E94E1B]">
          <div className="w-14 h-14 md:w-16 md:h-16 bg-[#0E1410] text-[#F2B23A] flex items-center justify-center shrink-0">
            <Route size={28} />
          </div>
          <div>
            <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#E94E1B]">
              Novetat · Nou circuit 2026
            </div>
            <div className="font-black text-2xl md:text-3xl uppercase leading-tight mt-1">
              <span className="text-[#E94E1B]">+240 metres</span> respecte el circuit antic
            </div>
            <p className="mt-2 text-sm md:text-base text-[#0E1410]/85 leading-relaxed">
              Aquest any el circuit del Cross de Carme ha tingut <strong>240 metres més
              de distància</strong> respecte l'antic recorregut, cosa que fa que les
              marques obtingudes tinguin encara més mèrit i que la comparativa amb els
              rècords històrics prengui un valor especial.
            </p>
          </div>
        </div>

        {/* Race narrative */}
        <div className="mt-14">
          <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A] mb-3">
            La cursa · minut a minut
          </div>
          <h3 className="font-black uppercase text-3xl md:text-4xl leading-tight mb-8">
            <span className="text-[#E94E1B]">Xavi Tomasa</span> deixa un crono de <span className="text-[#E94E1B]">19:11</span><br />
            que el situa en el millor ritme de l'història del Cross
          </h3>

          <div className="relative border-l-2 border-[#F2B23A]/40 pl-6 md:pl-10 space-y-8">
            {[
              {
                km: "Sortida",
                text: "La cursa començava amb un grup capdavanter amb Pau Nacenta agafant la primera posició, seguit d'Uri Ramírez, Jan Isla, Xavi Tomasa i Marc Espinar."
              },
              {
                km: "km 0,5",
                text: "Pau Nacenta cedeix la primera posició a Jan Isla, que agafa el comandament del grup capdavanter."
              },
              {
                km: "km 1,4",
                text: "Es manté un bon grup capdavanter d'uns 10 atletes. Jan Isla perd la primera posició en mans de Xavi Tomasa, que comença a agafar el timó de la cursa."
              },
              {
                km: "km 1,4 → 2,5",
                text: "Xavi Tomasa va obrint forat respecte Jan Isla, Oriol Marín, i també Marc Espinar dins del grup perseguidor."
              },
              {
                km: "km 2,5",
                text: "Xavi Tomasa acumula uns segons d'avantatge respecte Jan Isla, Marc Espinar i Oriol Marín. El top 4 comença a definir-se."
              },
              {
                km: "Últims km",
                text: "El top 4 queda clar. Més enrere, disputa pel top 5 entre Ferran Comas i Pau Nacenta."
              },
              {
                km: "Meta",
                text: "Xavi Tomasa creua la meta com a guanyador i supera el ritme al km d'Albert Ferrer del 2003. Jan Isla iguala el ritme de l'Albert Ferrer.",
                final: true
              },
            ].map((step, i) => (
              <div key={i} className="relative">
                <div
                  className={`absolute -left-[34px] md:-left-[50px] top-1 w-4 h-4 border-2 ${
                    step.final ? "bg-[#E94E1B] border-[#E94E1B]" : "bg-[#0E1410] border-[#F2B23A]"
                  }`}
                />
                <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A]">
                  {step.km}
                </div>
                <p className={`mt-1 text-base md:text-lg leading-relaxed ${step.final ? "font-black uppercase text-[#F2B23A] text-lg md:text-xl" : "text-[#F7F1E8]/85"}`}>
                  {step.text}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Footer note */}
        <div className="mt-12 bg-[#E94E1B] text-white p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-white/80">Seguirem informant</div>
            <div className="font-black text-xl md:text-2xl mt-1">
              Minut a minut · Web XIII Cross de Carme
            </div>
          </div>
          <span className="pulse-dot inline-flex items-center gap-2 bg-[#0E1410] px-5 py-3 font-display uppercase tracking-wider text-sm">
            <span className="w-2 h-2 bg-[#F2B23A] rounded-full" /> En directe
          </span>
        </div>
      </div>
    </section>
  );
};

const Marquee = () => (
  <div className="bg-[#E94E1B] text-white overflow-hidden py-4 border-y-2 border-[#0E1410]">
    <div className="marquee-track">
      {Array(2).fill(0).map((_, i) => (
        <div key={i} className="flex items-center gap-10 shrink-0 pr-10 font-display uppercase tracking-widest text-sm">
          {["Cursa 5,5 km", "★", "Caminada 8 km", "★", "Premi rècord 50€", "★", "DJ + Speaker en directe", "★", "4 juliol 2026", "★", "19:30h sortida", "★"].map((w, j) => (
            <span key={j}>{w}</span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const Portada = () => {
  const masculina = [
    { name: "Xavi Tomasa", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/3pvd19v4_3c02f10f-d8e2-4a36-99bc-85cae7743d5b.jpg", desc: "Un dels grans referents de l'atletisme català de fons. Campió de Catalunya de marató els anys 2018 i 2022, amb marques per sota dels 30 minuts en 10 km. Arriba a Carme després d'imposar-se a la Cursa Aquopolis Costa Daurada i es presenta com un dels principals candidats al triomf final.", highlight: true },
    { name: "Jan Isla", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/6w3fptjk_eb6e4883-097a-4e12-b274-6bf44d9c35b4.jpg", desc: "Jove atleta federat que ha demostrat un gran nivell aquesta temporada. Amb registres per sota dels 15 minuts en 5 km, 8è al Campionat d'Europa Up&Down i destacades actuacions en competicions catalanes de ruta. Recentment proclamat campió de la Pujada a les Dues Pedres en els 5 km. Tot i destacar especialment en curses de muntanya, la seva velocitat el converteix en un candidat molt seriós a la victòria.", highlight: true },
    { name: "Leo Contreras", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/qbkfwk3t_2009f00a-0fd9-43c0-9e68-051af2b2b711.jpg", desc: "L'argentí arriba a Carme sent campió de la 5 km d'Abrera i 4t a la Cornellà RCD Stadium per sota dels 15 minuts en 5 km. Les seves millors marques personals són 14'30\" en 5 km i 29'40\" en 10 km. L'atleta se situa en la sum per emportar-se el Cross.", highlight: true },
    { name: "Sergio Domínguez", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/73gjt404_f8558c91-2a40-4bc4-b598-fca0a1a9b4e9.jpg", desc: "Campió de Catalunya màster 40 de 5 km en ruta, amb marques personals de 15'50\" en 5 km i 32'50\" en 10 km. Recentment campió de la Cursa dels Masos. Un veterà de gran nivell que parteix com a candidat ferm al podi de la seva categoria." },
    { name: "Ferran Comas", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/5sbfxe7w_134c6aa4-f521-4aa8-b14c-686f0ad4ea59.jpg", desc: "Triatleta amb grans marques en atletisme, acredita 15'51\" en 5 km. La seva resistència i polivalència el converteixen en un dels noms a vigilar el dia de la prova." },
    { name: "Quim Vich", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/tb3vmc62_32a516d6-948d-452c-be6d-38276ffbb2a5.jpg", desc: "Marca de 15'58\" al Campionat de Catalunya en ruta de 5 km. Corredor especialitzat en curses d'orientació, aportarà una mirada estratègica i una gran capacitat de lectura del terreny." },
    { name: "Riduan Boulbayem", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/jr4n5u7h_44a36ad1-b4be-47d3-bfa5-b68f244ff00a.jpg", desc: "Registres de 15'55\" a la Cursa Popular d'Igualada, un dels noms emergents que arribarà a Carme amb ganes de fer-se un lloc a les primeres posicions." },
    { name: "Uri Ramírez", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/b32aip1t_fb6a9bee-6cd1-44c4-8b06-eeaee4cc239a.jpg", desc: "Recentment va baixar dels 16 minuts a la Cursa Nocturna de Cornellà (RCD Espanyol) i ha estat 3r als 10 km de la Pujada a les Dues Pedres. Tot i venir d'una llarga lesió, continua demostrant un gran potencial. Les seves millors marques personals: 14'52\" en 5 km i 30'18\" en 10 km." },
    { name: "Oriol Marín", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/oqdznlkk_79b5764e-3dd7-4b69-92bc-bcf04b3f139f.jpg", desc: "Jove atleta anoienc que aquesta temporada ha aconseguit una meritòria 6a posició al Trencacims (Campionats d'Espanya), és 3r d'Espanya en raquetes de neu i ha estat 3r per seleccions a la FEDME Astúries." },
    { name: "Pau Nacenta", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/krkn1pmx_acec0127-003a-439a-b2af-d73825db3faf.jpg", desc: "Arriba a Carme com a 1r del Spartan Beast Andorra, una de les proves d'obstacles més exigents del calendari. És un dels grans referents nacionals en curses d'obstacles (DEKA, Spartan), considerat entre els millors especialistes estatals de la disciplina." },
    { name: "Adri Martín", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/8j06n60i_cbea5b3d-d751-4bb4-9e2c-83dafe116043.jpg", desc: "Campió d'Espanya de l'OCR Series Espanya i 3r al OCR European Championship. Recentment 2n a la Pujada a Sant Marcial. Un dels grans referents nacionals en curses d'obstacles i un dels noms de pes del cartell." },
    { name: "Jordi Cifuentes", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/2ld1le6t_f0562ca5-d763-428a-b7cb-b48c0a0e20ba.jpg", desc: "El de Marganell, un clàssic que entrena per les muntanyes de Montserrat, s'ha imposat en curses com la Sant Mateo Xtrail, la Batega el Bac, els Corriols de Foc, la Moixeró Trail, la 5 km de Puigcerdà i la Pujada a Guanta. Un dels noms més contrastats del trail català." },
  ];
  const masculinaExtra = "La nòmina d'aspirants es completa amb el veterà Joan Rosich i corredors contrastats com Camilo Gallego, Ayrton Fontecha, José Do Prado, Juan Alarcón, Albert Lao, Fernando Chamorro, Carlos Gámez, Josep Fabregat i David Busquets.";
  const sub18 = [
    { name: "Bernat Rueda", desc: "Un dels grans favorits en categoria Sub 18, amb una progressió molt destacada aquesta temporada." },
    { name: "Martí Pain", desc: "Jove valor de l'atletisme local que arriba en gran moment de forma i lluitarà per les primeres posicions de la categoria." },
  ];

  const femenina = [
    { name: "Ahlam Elkakdi", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/yz0stkzp_fc9f9261-647f-4d70-9218-3509b8c019ea.jpg", desc: "Una de les corredores més destacades del panorama català i estatal. Recentment proclamada campiona d'Espanya de Trail Series i top 5 a la Garmin Epic Trail Barruera de 24 km, consolidant-se com una de les atletes més completes del moment.", highlight: true },
    { name: "Anna Noguera", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/861ueyoo_IMG_4760.jpeg", desc: "Campiona d'Espanya de triatló de mitja distància. Una de les grans favorites de la categoria femenina, amb un palmarès impressionant en proves d'ultra resistència que la converteixen en una de les atletes més completes del cartell.", highlight: true },
    { name: "Jessica Ruiz", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/7h6s2idi_1896c059-0a26-4d66-a14b-76abec66c6ec.jpg", desc: "La seva passió pel trail la porta a poder dir que és 2a a la prestigiosa cursa de l'Olla Vertical, una de les proves de muntanya més exigents del calendari. Recentment ha fet podi a la Milla Urbana de Sant Pere la Segrera." },
    { name: "Míriam López", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/kmletzxn_242f3d1a-e77e-4da7-a1b5-bdd1eb8a71cb.jpg", desc: "Campiona de la Formentera All Round Trail i subcampiona de la Trail Bronchales, amb millors marques personals de 17'43\" en 5 km i 36'37\" en 10 km. Una corredora completa que combina trail i velocitat en ruta." },
    { name: "Lídia Ramos", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/omzlcuhq_9027638a-2946-4d13-a6d5-f2808ee0d1b1.jpg", desc: "Corredora de trail de Vilanova i la Geltrú que arriba a Carme sent campiona de la Trencacims 54 km, amb un desnivell positiu de 4.300 m, i top 10 a la Garmin Epic Trail Barruera. Una de les especialistes en distàncies llargues més contrastades." },
    { name: "Airina Guillén", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/76n6kmw1_4241a279-4c40-4171-ac06-1011805c07f2.jpg", desc: "Una clàssica de la ruta i l'asfalt, i parella de Fernando Chamorro (també atleta del cartell amb marques sub-16' antigament). Va ser campiona de la 5 km Diagonal i acredita una millor marca de 38'30\" en 10 km a la Cursa dels Nassos." },
    { name: "Maite Ríos", photo: "https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/7ngz0eiv_d9043dc8-9aea-480f-8b69-e7c557a410ac.jpg", desc: "Arriba de fa relativament poc amb un temps de 37'36\" a la Cursa del Corte Inglés. Les seves millors marques personals: 17'45\" en 5 km i 36'42\" en 10 km." },
    { name: "Mamen del Castillo, Aroa Meda i Lorena Madueño", desc: "Completen la llista de candidates a les primeres posicions i aportaran encara més competitivitat a una categoria femenina que promet una lluita molt ajustada pel podi." },
  ];

  return (
    <section
      id="portada"
      data-testid="portada-section"
      className="relative bg-[#F7F1E8] text-[#15140F] py-20 md:py-28 px-5 sm:px-8 overflow-hidden"
    >
      {/* Background diagonal stripes accent */}
      <div className="absolute -top-20 -left-20 w-72 h-72 stripes opacity-[0.08] rotate-12 pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 stripes opacity-[0.06] -rotate-12 pointer-events-none" />

      <div className="relative max-w-7xl mx-auto">
        {/* Newspaper header */}
        <div className="border-b-4 border-[#0E1410] pb-4 mb-10 flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Newspaper size={20} className="text-[#E94E1B]" />
            <span className="font-display uppercase tracking-[0.3em] text-xs text-[#15140F]">
              Portada · Actualitat
            </span>
          </div>
          <div className="font-display uppercase tracking-widest text-[10px] text-[#6B6358]">
            A 6 dies de la cursa
          </div>
        </div>

        {/* Big title */}
        <div className="mb-10">
          <Logo size="xl" />
          <h2 className="font-black uppercase text-3xl sm:text-5xl md:text-[3.6rem] leading-[1.02] mt-7 max-w-5xl">
            Més de 200 participants i un <span className="text-[#E94E1B]">cartell de luxe</span> al XIII Cross de Carme
          </h2>
        </div>

        {/* Lead paragraph */}
        <div className="border-y border-[#15140F]/15 py-6 mb-12 max-w-4xl">
          <p className="text-lg md:text-xl leading-relaxed text-[#15140F]/85 first-letter:font-black first-letter:text-5xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.9] first-letter:text-[#E94E1B]">
            Quan falten només <strong>6 dies</strong> per a la celebració del XIII Cross de Carme,
            l'organització preveu una participació que <strong>superarà els 200 inscrits</strong>,
            una xifra que confirma el gran moment que viu la prova i que podria convertir
            aquesta edició en la de <em>major participació de la història</em> del Cross de Carme.
          </p>
        </div>

        {/* Two-column layout */}
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14">
          {/* MASCULINA */}
          <div className="border-t-2 border-[#0E1410] pt-5">
            <div className="flex items-center gap-3 mb-6">
              <Flame size={20} className="text-[#E94E1B]" />
              <h3 className="font-display font-bold uppercase tracking-[0.18em] text-lg text-[#E94E1B]">
                Categoria Masculina
              </h3>
            </div>

            <ul className="space-y-5">
              {masculina.map((a, i) => {
                if (a.mystery) {
                  return (
                    <li key={a.name + i} className="relative bg-[#0E1410] text-[#F7F1E8] p-6 md:p-7 my-3 overflow-hidden border-l-4 border-[#F2B23A]">
                      <div className="absolute -top-6 -right-6 w-32 h-32 stripes opacity-25 rotate-12 pointer-events-none" />
                      <div className="relative flex items-start gap-5">
                        <div className="shrink-0 w-16 h-16 md:w-20 md:h-20 border-2 border-[#F2B23A] flex items-center justify-center font-black text-4xl md:text-5xl text-[#F2B23A] animate-pulse">
                          ?
                        </div>
                        <div className="min-w-0">
                          <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A]">
                            Pròxima incorporació
                          </div>
                          <div className="font-black text-2xl md:text-3xl uppercase tracking-tight leading-tight mt-1">
                            Atleta sorpresa
                          </div>
                          <p className="mt-2 text-sm md:text-[15px] text-[#F7F1E8]/85 leading-relaxed">
                            {a.desc}
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                }
                return (
                  <li key={a.name + i} className="flex gap-4 group">
                    <Star
                      size={20}
                      className={`shrink-0 mt-1 ${a.highlight ? "text-[#E94E1B] fill-[#E94E1B]" : "text-[#E94E1B]"}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-black text-xl md:text-2xl uppercase tracking-tight leading-tight">
                        {a.name}
                      </div>
                      {a.photo ? (
                        <div className="mt-2 flex gap-4 items-start">
                          <img
                            src={a.photo}
                            alt={a.name}
                            loading="lazy"
                            className="w-24 h-32 sm:w-28 sm:h-36 object-cover border-2 border-[#0E1410] shrink-0"
                            style={{ filter: "saturate(1.05)", objectPosition: "center top" }}
                          />
                          <p className="text-sm md:text-[15px] text-[#15140F]/80 leading-relaxed">
                            {a.desc}
                          </p>
                        </div>
                      ) : (
                        <p className="mt-1.5 text-sm md:text-[15px] text-[#15140F]/80 leading-relaxed">
                          {a.desc}
                        </p>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            <p className="mt-7 pt-5 border-t border-dashed border-[#15140F]/20 text-sm text-[#6B6358] leading-relaxed">
              {masculinaExtra}
            </p>

            {/* Sub 18 highlight - discret */}
            <div className="mt-6 pt-5 border-t border-[#15140F]/15">
              <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#E94E1B] mb-2">
                Categoria Sub 18
              </div>
              <p className="text-sm text-[#15140F]/80 leading-relaxed">
                Entre els favorits hi trobem <strong>Bernat Rueda</strong> (top 10 a la Garmin Epic Trail 12 km) i <strong>Martí Pain</strong>,
                dos joves valors amb una progressió molt destacada aquesta temporada.
              </p>
            </div>
          </div>

          {/* FEMENINA */}
          <div className="border-t-2 border-[#0E1410] pt-5">
            <div className="flex items-center gap-3 mb-6">
              <Flame size={20} className="text-[#E94E1B]" />
              <h3 className="font-display font-bold uppercase tracking-[0.18em] text-lg text-[#E94E1B]">
                Categoria Femenina
              </h3>
            </div>

            <ul className="space-y-5">
              {femenina.map((a) => (
                <li key={a.name} className="flex gap-4">
                  <Star
                    size={20}
                    className={`shrink-0 mt-1 ${a.highlight ? "text-[#E94E1B] fill-[#E94E1B]" : "text-[#E94E1B]"}`}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="font-black text-xl md:text-2xl uppercase tracking-tight leading-tight">
                      {a.name}
                    </div>
                    {a.photo ? (
                      <div className="mt-2 flex gap-4 items-start">
                        <img
                          src={a.photo}
                          alt={a.name}
                          loading="lazy"
                          className="w-24 h-32 sm:w-28 sm:h-36 object-cover border-2 border-[#0E1410] shrink-0"
                          style={{ filter: "saturate(1.05)", objectPosition: "center top" }}
                        />
                        <p className="text-sm md:text-[15px] text-[#15140F]/80 leading-relaxed">
                          {a.desc}
                        </p>
                      </div>
                    ) : (
                      <p className="mt-1.5 text-sm md:text-[15px] text-[#15140F]/80 leading-relaxed">
                        {a.desc}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {/* Entrega premis */}
            <div className="mt-8 bg-[#0E1410] text-[#F7F1E8] p-6 border-l-4 border-[#F2B23A]">
              <div className="flex items-center gap-2 font-display uppercase tracking-[0.2em] text-xs text-[#F2B23A]">
                <Medal size={16} /> Entrega de premis
              </div>
              <p className="mt-3 text-sm leading-relaxed">
                L'entrega de premis comptarà amb la presència del terrassenc{" "}
                <strong className="text-white">Bartolomé Serrano</strong>, exatleta internacional
                i subcampió del món de mitja marató, que serà l'encarregat de lliurar els
                trofeus als vencedors d'aquesta edició del Cross de Carme.
              </p>
            </div>

            {/* Cartell de luxe */}
            <div className="mt-6 bg-[#E94E1B] text-white p-6">
              <div className="font-display uppercase tracking-[0.2em] text-xs text-white/80">
                Un cartell de luxe
              </div>
              <p className="mt-3 text-sm leading-relaxed">
                Amb aquest cartell d'atletes, el XIII Cross de Carme presentarà un dels nivells
                esportius més alts de la seva història. La combinació de corredors d'elit, joves
                promeses, especialistes en ruta, muntanya, orientació i curses d'obstacles
                garanteix una jornada de gran espectacle esportiu.
              </p>
            </div>
          </div>
        </div>

        {/* Closing pull-quote */}
        <div className="mt-14 max-w-4xl border-l-4 border-[#E94E1B] pl-6 md:pl-8">
          <p className="font-display text-2xl md:text-3xl leading-snug uppercase tracking-tight text-[#15140F]">
            A falta de 6 dies per a la prova i amb una previsió que <span className="text-[#E94E1B]">supera els 200 corredors</span>,
            Carme es prepara per viure una nova gran festa de l'atletisme popular.
          </p>
          <p className="mt-4 text-[#6B6358] text-sm">
            La lluita per la victòria promet ser intensa: qualsevol detall pot acabar decidint
            una de les edicions més competitives que es recorden.
          </p>
        </div>

        {/* Disclaimer */}
        <div className="mt-8 max-w-4xl bg-[#F2B23A]/15 border-l-2 border-[#F2B23A] p-4 md:p-5 flex gap-3 items-start">
          <AlertTriangle size={18} className="text-[#E94E1B] shrink-0 mt-0.5" />
          <p className="text-xs md:text-sm text-[#6B6358] leading-relaxed">
            <strong className="text-[#15140F]">Nota:</strong> L'organització podria haver-se deixat algun atleta destacat
            per despiste o per no haver pogut obtenir totes les dades dels inscrits al Cross. Si és el teu cas, contacta amb
            nosaltres a través de la <a href="#contacte" className="underline hover:text-[#E94E1B]">secció de contacte</a> i
            us afegirem encantats.
          </p>
        </div>

        {/* CTA */}
        <div className="mt-10 flex flex-wrap gap-4">
          <a
            href="https://www.weevens.com/esdeveniments/cross-de-carme/"
            target="_blank"
            rel="noreferrer"
            className="px-7 py-4 bg-[#0E1410] text-white font-display uppercase tracking-wider text-sm inline-flex items-center gap-3 hover:bg-[#E94E1B] transition-colors"
          >
            Inscriu-te tu també <ExternalLink size={16} />
          </a>
          <a
            href="#categories"
            className="px-7 py-4 border-2 border-[#0E1410] text-[#0E1410] font-display uppercase tracking-wider text-sm hover:bg-[#0E1410] hover:text-white transition-colors"
          >
            Veure categories
          </a>
        </div>
      </div>
    </section>
  );
};

const InfoCursa = () => (
  <Section
    id="info"
    eyebrow="Informació de la Cursa"
    title="Tot el que cal saber"
    intro="Horaris, recorregut i informació clau perquè res no et sorprengui el dia de la cursa."
  >
    <div className="grid lg:grid-cols-2 gap-8">
      {/* Timeline */}
      <div className="bg-white border border-[#15140F]/10 p-8">
        <h3 className="font-display uppercase tracking-wider text-xl mb-6 flex items-center gap-3">
          <Clock size={22} className="text-[#E94E1B]" /> Horaris
        </h3>
        <ol className="space-y-6">
          {[
            { h: "18:15 – 19:15", t: "Entrega de dorsals", p: "Piscina Municipal de Carme" },
            { h: "19:30", t: "Sortida cursa i caminada", p: "Piscina Municipal de Carme" },
            { h: "19:30+", t: "Arribada a meta", p: "Piscina Municipal de Carme" },
          ].map((step, i) => (
            <li key={i} className="flex gap-5">
              <div className="font-black text-3xl text-[#E94E1B] min-w-[88px] leading-none pt-1">
                {step.h}
              </div>
              <div className="border-l-2 border-[#15140F]/10 pl-5">
                <div className="font-display uppercase tracking-wider text-base">{step.t}</div>
                <div className="text-sm text-[#6B6358] mt-1">{step.p}</div>
              </div>
            </li>
          ))}
        </ol>
      </div>

      {/* Recorregut */}
      <div className="bg-[#0E1410] text-[#F7F1E8] p-8 relative overflow-hidden">
        <div className="absolute -top-10 -right-10 w-40 h-40 stripes opacity-30" />
        <h3 className="font-display uppercase tracking-wider text-xl mb-6 flex items-center gap-3">
          <Route size={22} className="text-[#F2B23A]" /> Recorregut
        </h3>
        <div className="space-y-5">
          <div className="border-l-4 border-[#E94E1B] pl-5">
            <div className="font-display uppercase tracking-widest text-xs text-[#F2B23A]">Cros</div>
            <div className="font-black text-2xl mt-1">5,5 km</div>
            <p className="text-sm text-[#F7F1E8]/70 mt-2">
              90% pista de terra · 10% asfalt · 150 m de desnivell positiu
            </p>
          </div>
          <div data-testid={HOME.caminadaInfoCard} className="border-l-4 border-[#F2B23A] pl-5">
            <div className="font-display uppercase tracking-widest text-xs text-[#F2B23A]">Caminada</div>
            <div className="font-black text-2xl mt-1 flex items-center gap-2">
              ~8 km <Mountain size={20} className="text-[#F2B23A]" />
            </div>
            <p className="text-sm text-[#F7F1E8]/70 mt-2">
              Recorregut per <strong>senderes</strong>, amb <strong>vistes al riu</strong> i
              <strong> panoràmiques de Carme</strong>. Enllaça amb el cros al <strong>km 5</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  </Section>
);

const Inscripcions = () => (
  <Section
    id="inscripcions"
    eyebrow="Inscripcions"
    title="Inscriu-te al XIII Cross"
    intro="Un únic preu, sense complicacions. Apunta't ja i assegura el teu dorsal."
    testId={HOME.inscripcionsSection}
    dark
  >
    <div className="grid md:grid-cols-2 gap-6 max-w-4xl">
      {/* Single price */}
      <div data-testid={HOME.inscripcionsPriceCard} className="bg-[#E94E1B] text-white p-10 relative overflow-hidden group hover-grow">
        <div className="absolute top-4 right-4 stamp text-white">Preu únic</div>
        <div className="font-display uppercase tracking-widest text-xs text-white/80">Cursa / Caminada</div>
        <div className="mt-5 font-black text-[6rem] leading-none">10€</div>
        <p className="mt-4 text-white/90">
          Inscripció única per a la cursa o la caminada. Bossa del corredor inclosa.
        </p>
        <a
          href="https://www.weevens.com/esdeveniments/cross-de-carme/"
          target="_blank"
          rel="noreferrer"
          className="mt-8 inline-flex items-center gap-2 bg-white text-[#0E1410] px-6 py-3 font-display uppercase tracking-wider text-sm hover:bg-[#F2B23A] transition-colors"
        >
          Inscriu-te ara <ExternalLink size={14} />
        </a>
      </div>

      {/* Samarretes esgotades */}
      <div data-testid={HOME.inscripcionsSamarretaCard} className="border-2 border-dashed border-[#F2B23A]/50 p-10 relative overflow-hidden">
        <Shirt size={48} className="text-[#F2B23A]/40" />
        <div className="mt-5 font-display uppercase tracking-widest text-xs text-[#F2B23A]">Samarretes</div>
        <div className="mt-3 font-black text-4xl md:text-5xl uppercase text-[#F7F1E8]">
          Esgotades
        </div>
        <p className="mt-5 text-[#F7F1E8]/70">
          Les samarretes oficials del XIII Cross de Carme ja s'han <strong>esgotat</strong>.
          Gràcies a tothom que les ha reservat amb antelació! Et podràs inscriure igualment
          a la cursa o la caminada amb el preu únic.
        </p>
        <div className="mt-6 inline-flex items-center gap-2 text-[#F2B23A] text-sm font-display uppercase tracking-wider">
          <AlertTriangle size={16} /> Estoc finalitzat
        </div>
      </div>
    </div>
  </Section>
);

const Categories = () => {
  const cats = [
    { title: "General", sub: "Totes les edats", prizes: ["Trofeu + 150€", "Trofeu + 110€", "Trofeu + 100€"], featured: true },
    { title: "Menors", sub: "Fins a 17 anys", prizes: ["Trofeu", "Trofeu", "Trofeu"] },
    { title: "Sènior", sub: "18 a 39 anys", prizes: ["Trofeu", "Trofeu", "Trofeu"] },
    { title: "Veterans", sub: "+40 anys", prizes: ["Trofeu", "Trofeu", "Trofeu"] },
    { title: "Màster 50", sub: "+50 anys · M & F", prizes: ["Trofeu", "Trofeu", "Trofeu"] },
    { title: "Local", sub: "Residents de Carme", prizes: ["Trofeu"] },
  ];
  return (
    <Section
      id="categories"
      eyebrow="Categories i Premis"
      title="Cada corredor, la seva categoria"
      intro="Compet en la teva categoria i opta a trofeus i premis en metàl·lic."
      testId={HOME.categoriesSection}
    >
      {/* Record card */}
      <div className="bg-[#0E1410] text-[#F7F1E8] p-7 md:p-9 mb-12 grid md:grid-cols-[1.2fr_1fr_1fr] gap-6 items-center border-l-4 border-[#F2B23A]">
        <div>
          <div className="font-display uppercase tracking-[0.25em] text-xs text-[#F2B23A] flex items-center gap-2">
            <Sparkles size={14} /> Premi especial
          </div>
          <div className="font-black text-3xl md:text-4xl mt-2 uppercase">
            +50€ si bats <span className="text-[#E94E1B]">el rècord</span>
          </div>
          <p className="text-sm text-[#F7F1E8]/70 mt-2">
            Per al primer absolut masculí o femení que superi el rècord històric.
          </p>
        </div>
        <div className="border-l border-white/15 pl-6">
          <div className="font-display uppercase tracking-widest text-xs text-[#F2B23A]">Rècord masculí</div>
          <div className="font-black text-3xl mt-1">18′47″</div>
        </div>
        <div className="border-l border-white/15 pl-6">
          <div className="font-display uppercase tracking-widest text-xs text-[#F2B23A]">Rècord femení</div>
          <div className="font-black text-3xl mt-1">23′17″</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {cats.map((c) => (
          <div
            key={c.title}
            className={`p-7 border ${
              c.featured
                ? "bg-[#E94E1B] text-white border-[#E94E1B] lg:row-span-2 lg:col-span-1"
                : "bg-white border-[#15140F]/10"
            }`}
          >
            <div className="flex items-start justify-between">
              <div>
                <div className={`font-display uppercase tracking-wider text-xs ${c.featured ? "text-white/80" : "text-[#E94E1B]"}`}>
                  {c.sub}
                </div>
                <div className="font-black text-3xl mt-1 uppercase">{c.title}</div>
              </div>
              <Trophy size={28} className={c.featured ? "text-white" : "text-[#F2B23A]"} />
            </div>
            <ul className="mt-6 space-y-3">
              {c.prizes.map((p, i) => (
                <li key={i} className={`flex items-center gap-3 ${c.featured ? "text-white" : "text-[#15140F]"}`}>
                  <span className={`w-8 h-8 flex items-center justify-center font-display font-bold ${
                    c.featured ? "bg-[#0E1410] text-[#F2B23A]" : "bg-[#0E1410] text-white"
                  }`}>
                    {i + 1}
                  </span>
                  <span className="font-medium">{p}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      <div className="mt-8 inline-flex items-center gap-2 text-sm text-[#6B6358]">
        <AlertTriangle size={14} className="text-[#E94E1B]" /> Els premis no són acumulables.
      </div>
    </Section>
  );
};

const Serveis = () => {
  const items = [
    { icon: Backpack, t: "Bossa del corredor", d: "Tots els participants reben la seva bossa amb obsequi." },
    { icon: Droplet, t: "Avituallaments", d: "Líquid i sòlid al km 3, i avituallament final a la meta." },
    { icon: Shirt, t: "Guarda-roba", d: "Zona de custòdia a la sortida i arribada." },
    { icon: Heart, t: "Dutxes i piscina", d: "Disponibles a l'arribada per refrescar-te i descansar." },
    { icon: Music, t: "DJ en directe", d: "Música i ambient des de l'inici fins a l'entrega de premis." },
    { icon: Camera, t: "Fotografia + Dron", d: "Captures professionals i vistes aèries de la cursa." },
  ];
  return (
    <Section
      id="serveis"
      eyebrow="Serveis i Experiència"
      title="Tot preparat per a tu"
      intro="Una cursa amb tots els detalls perquè et concentris només a córrer i gaudir."
      testId={HOME.serveisSection}
      dark
    >
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10 border border-white/15">
        {items.map(({ icon: Icon, t, d }) => (
          <div key={t} className="bg-[#0E1410] p-8 hover:bg-[#15140F] transition-colors group">
            <Icon size={36} className="text-[#E94E1B] group-hover:text-[#F2B23A] transition-colors" />
            <div className="mt-5 font-display uppercase tracking-wider text-lg">{t}</div>
            <p className="mt-2 text-sm text-[#F7F1E8]/65">{d}</p>
          </div>
        ))}
      </div>
    </Section>
  );
};

const Galeria = () => {
  const photos = [
    { src: IMAGES.founder, c: "Jordi Plandolit · Fundador 1996" },
    { src: IMAGES.participants, c: "Participants amb dorsals" },
    { src: IMAGES.winner, c: "Creuant la meta victoriós" },
    { src: IMAGES.meta, c: "Arribada a meta" },
    { src: IMAGES.family, c: "Emoció i família" },
    { src: IMAGES.veterans, c: "Veterans · edicions històriques" },
    { src: IMAGES.joy, c: "Celebració a l'arribada" },
  ];
  return (
    <Section
      id="galeria"
      eyebrow="Moments de la cursa"
      title="Història en imatges"
      intro="Recordem els millors moments de les edicions anteriors del Cross de Carme."
      testId={HOME.galeriaSection}
    >
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        {photos.map((p, i) => (
          <figure
            key={i}
            className={`group relative overflow-hidden bg-[#0E1410] ${
              i === 0 ? "md:row-span-2 md:col-span-2 aspect-[4/3] md:aspect-auto" : "aspect-[4/3]"
            }`}
          >
            <img src={p.src} alt={p.c} className="w-full h-full object-cover hover-grow" />
            <figcaption className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 via-black/40 to-transparent text-white text-sm font-display uppercase tracking-wider">
              {p.c}
            </figcaption>
          </figure>
        ))}
      </div>
    </Section>
  );
};

const Classificacio = () => {
  // Palmarès complet · edicions anteriors. Editable.
  const palmaresM = [
    { edicio: "XIII", any: 2026, guanyador: "Xavi Tomasa", temps: "19:11" },
    { edicio: "XII", any: 2025, guanyador: "Francesc Sedó", temps: "21:17" },
    { edicio: "XI", any: 2024, guanyador: "Roger Vives", temps: "21:35" },
    { edicio: "X", any: 2023, guanyador: "David Borràs", temps: "21:58" },
    { edicio: "IX", any: 2022, guanyador: "David Borràs", temps: "21:36" },
    { gap: true },
    { edicio: "VIII", any: 2003, guanyador: "Francisco Llamas", temps: "19:25" },
    { edicio: "VII", any: 2002, guanyador: "Albert Ferrer", temps: "18:47" },
    { edicio: "VI", any: 2001, guanyador: "Albert Ferrer", temps: "19:20" },
    { edicio: "V", any: 2000, guanyador: "Albert Ferrer", temps: "19:55" },
    { edicio: "IV", any: 1999, guanyador: "Albert Ferrer", temps: "20:01" },
    { edicio: "III", any: 1998, guanyador: "José Parra", temps: "19:38" },
    { edicio: "II", any: 1997, guanyador: "Roger Roca", temps: "20:05" },
    { edicio: "I", any: 1996, guanyador: "Francisco Llamas", temps: "20:36" },
  ];
  const palmaresF = [
    { edicio: "XIII", any: 2026, guanyadora: "Anna Noguera", temps: "24:10" },
    { edicio: "XII", any: 2025, guanyadora: "Marisol Ferragut", temps: "43:18" },
    { edicio: "XI", any: 2024, guanyadora: "Laura Mercader", temps: "32:35" },
    { edicio: "X", any: 2023, guanyadora: "Elena Segura", temps: "29:57" },
    { edicio: "IX", any: 2022, guanyadora: "Montserrat Rocaginé", temps: "32:05" },
    { gap: true },
    { edicio: "VIII", any: 2003, guanyadora: "Carme Tort", temps: "23:17" },
    { edicio: "VII", any: 2002, guanyadora: "Esperanza Zuriguel", temps: "24:32" },
    { edicio: "VI", any: 2001, guanyadora: "Marisol Oliva", temps: "26:05" },
    { edicio: "V", any: 2000, guanyadora: "Marisol Oliva", temps: "26:06" },
    { edicio: "IV", any: 1999, guanyadora: "Pilar Palacios", temps: "27:16" },
    { edicio: "III", any: 1998, guanyadora: "Nancy Quispe", temps: "25:13" },
    { edicio: "II", any: 1997, guanyadora: "Lolita Nieto", temps: "28:23" },
    { edicio: "I", any: 1996, guanyadora: "Lolita Nieto", temps: "29:04" },
  ];

  const PalmaresTable = ({ rows, femaleKey }) => (
    <div className="mt-4 border border-[#15140F]/10 divide-y divide-[#15140F]/10 overflow-hidden bg-white">
      <div className="grid grid-cols-[70px_60px_1fr_80px] bg-[#0E1410] text-[#F2B23A] font-display uppercase tracking-widest text-[10px]">
        <div className="px-3 py-2">Edició</div>
        <div className="px-3 py-2">Any</div>
        <div className="px-3 py-2">Guanyador{femaleKey ? "a" : ""}</div>
        <div className="px-3 py-2 text-right">Temps</div>
      </div>
      {rows.map((r, idx) => {
        if (r.gap) {
          return (
            <div key={`gap-${idx}`} className="grid grid-cols-1 bg-[#0E1410]/5">
              <div className="px-3 py-2 text-center font-display uppercase tracking-[0.28em] text-[10px] text-[#6B6358]">
                · Aturada de l'esdeveniment ·
              </div>
            </div>
          );
        }
        const isLatest = r.any === 2026;
        return (
          <div
            key={r.edicio + r.any}
            className={`grid grid-cols-[70px_60px_1fr_80px] items-center text-sm ${
              isLatest ? "bg-[#E94E1B]/10 font-black" : ""
            }`}
          >
            <div className={`px-3 py-2 font-display font-bold tracking-wider ${isLatest ? "text-[#E94E1B]" : "text-[#15140F]"}`}>{r.edicio}</div>
            <div className="px-3 py-2 tabular-nums text-[#6B6358]">{r.any}</div>
            <div className={`px-3 py-2 font-display ${isLatest ? "uppercase" : ""} ${r[femaleKey ? "guanyadora" : "guanyador"] === "—" ? "text-[#6B6358]/60" : "text-[#15140F]"}`}>
              {r[femaleKey ? "guanyadora" : "guanyador"]}
            </div>
            <div className="px-3 py-2 text-right tabular-nums text-[#15140F]">{r.temps}</div>
          </div>
        );
      })}
    </div>
  );

  return (
    <Section
      id="classificacio"
      eyebrow="Classificació històrica"
      title="Els guanyadors absoluts"
      intro="Tots els campions i campiones que han escrit la història del Cross de Carme."
      testId={HOME.classificacioSection}
      dark
    >
      {/* Nou palmarès 2026 */}
      <div className="mb-10 bg-[#E94E1B] text-white p-6 md:p-8 border-l-4 border-[#F2B23A]">
        <div className="flex items-center gap-2 font-display uppercase tracking-[0.25em] text-[10px] text-[#F2B23A]">
          <Trophy size={14} /> Palmarès XIII edició · 2026
        </div>
        <div className="mt-5 grid md:grid-cols-2 gap-6">
          <div className="border-l-2 border-white/40 pl-5 flex gap-4 items-start">
            <img
              src="https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/ucwvb2o6_IMG_5003.jpeg"
              alt="Xavi Tomasa · guanyador XIII Cross de Carme"
              loading="lazy"
              className="w-24 h-32 sm:w-28 sm:h-36 object-cover border-2 border-[#0E1410] shrink-0"
              style={{ objectPosition: "center top" }}
            />
            <div className="min-w-0">
              <div className="font-display uppercase tracking-widest text-xs text-white/85">Guanyador masculí</div>
              <div className="font-black text-2xl md:text-3xl mt-1 uppercase leading-tight">Xavi Tomasa</div>
              <div className="mt-2 inline-flex items-center gap-2 bg-[#0E1410] text-[#F2B23A] px-3 py-1 font-display font-bold text-xl tabular-nums">
                19:11
              </div>
            </div>
          </div>
          <div className="border-l-2 border-white/40 pl-5 flex gap-4 items-start">
            <img
              src="https://customer-assets.emergentagent.com/job_crossfit-carme-1/artifacts/ksi7z18i_IMG_5005.jpeg"
              alt="Anna Noguera · guanyadora XIII Cross de Carme"
              loading="lazy"
              className="w-24 h-32 sm:w-28 sm:h-36 object-cover border-2 border-[#0E1410] shrink-0"
              style={{ objectPosition: "center top" }}
            />
            <div className="min-w-0">
              <div className="font-display uppercase tracking-widest text-xs text-white/85">Guanyadora femenina</div>
              <div className="font-black text-2xl md:text-3xl mt-1 uppercase leading-tight">Anna Noguera</div>
              <div className="mt-2 inline-flex items-center gap-2 bg-[#0E1410] text-[#F2B23A] px-3 py-1 font-display font-bold text-xl tabular-nums">
                24:10
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white text-[#15140F] p-3 md:p-4">
          <div className="bg-[#E94E1B] text-white px-5 py-3 font-display uppercase tracking-widest text-sm">
            Classificació Absoluta Masculina
          </div>
          <PalmaresTable rows={palmaresM} />
        </div>
        <div className="bg-white text-[#15140F] p-3 md:p-4">
          <div className="bg-[#F2B23A] text-[#0E1410] px-5 py-3 font-display uppercase tracking-widest text-sm">
            Classificació Absoluta Femenina
          </div>
          <PalmaresTable rows={palmaresF} femaleKey />
        </div>
      </div>
      <div className="mt-8 text-center">
        <a
          href="https://sportmaniacs.com/es/services/inscription/xiii-cross-de-carme-i-iv-marxa-de-carme-ets-tu"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 border border-white/40 text-white px-6 py-3 font-display uppercase tracking-wider text-sm hover:bg-white hover:text-[#0E1410] transition-colors"
        >
          Classificacions completes · Sportmaniacs <ExternalLink size={14} />
        </a>
      </div>
    </Section>
  );
};

const Normativa = () => {
  const rules = [
    { icon: Eye, t: "Dorsal visible obligatori", d: "Has de portar el dorsal sempre visible durant tota la cursa." },
    { icon: Droplet, t: "Pas pels avituallaments", d: "Cal seguir l'ordre i les indicacions del personal." },
    { icon: Footprints, t: "Respecte al recorregut", d: "No es pot abandonar el circuit oficial marcat." },
    { icon: Users, t: "Comportament", d: "Esportivitat i respecte cap a corredors, voluntaris i veïns." },
    { icon: ShieldAlert, t: "Decisions de l'organització", d: "L'organització té la decisió final en cas de conflicte." },
    { icon: Camera, t: "Drets d'imatge", d: "En inscriure't acceptes l'ús de fotos/vídeos amb finalitat promocional. Si no vols sortir-hi, comunica-ho prèviament." },
  ];
  return (
    <Section
      id="normativa"
      eyebrow="Normativa"
      title="Regles que cal complir"
      intro="Assegura't de respectar aquesta normativa per gaudir d'una cursa segura per a tothom."
      testId={HOME.normativaSection}
    >
      <div className="grid md:grid-cols-2 gap-px bg-[#15140F]/10 border border-[#15140F]/10">
        {rules.map(({ icon: Icon, t, d }) => (
          <div key={t} className="bg-white p-7 flex gap-5">
            <div className="shrink-0 w-12 h-12 bg-[#0E1410] text-[#F2B23A] flex items-center justify-center">
              <Icon size={22} />
            </div>
            <div>
              <div className="font-display uppercase tracking-wider text-base">{t}</div>
              <p className="text-sm text-[#6B6358] mt-2">{d}</p>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

const Organitzacio = () => (
  <Section
    id="organitzacio"
    eyebrow="L'Organització"
    title="La gent que ho fa possible"
    intro="Darrere del Cross de Carme hi ha un equip dedicat que any rere any treballa amb il·lusió perquè aquesta cursa sigui un èxit."
    testId={HOME.organitzacioSection}
    dark
  >
    <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
      <div className="relative overflow-hidden">
        <img src={IMAGES.team} alt="Equip organitzador" className="w-full h-full object-cover" />
        <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/90 to-transparent">
          <div className="font-display uppercase tracking-widest text-xs text-[#F2B23A]">Equip</div>
          <div className="font-black text-2xl mt-1">Carme ets Tu · Training Club Esportiu Orpí · Ajuntament de Carme</div>
        </div>
      </div>
      <div className="bg-[#15140F] p-8 border-l-4 border-[#E94E1B]">
        <img src={IMAGES.medir} alt="Medir Ros" className="w-32 h-32 object-cover rounded-full" />
        <div className="font-display uppercase tracking-widest text-xs text-[#F2B23A] mt-5">President · Organitzador</div>
        <div className="font-black text-3xl mt-2">Medir Ros</div>
        <div className="text-[#F7F1E8]/60 text-sm mt-1">Carme ets Tu · XIII Cross de Carme</div>
        <p className="mt-5 text-[#F7F1E8]/80 text-sm leading-relaxed">
          Amb una dedicació incansable i una passió per l'esport comunitari,
          Medir lidera l'organització del Cross de Carme any rere any.
        </p>
      </div>
    </div>
  </Section>
);

const Patrocinadors = () => (
  <Section
    id="patrocinadors"
    eyebrow="Patrocinadors"
    title="Gràcies pel suport"
    intro="Gràcies a aquestes entitats fem possible el Cross de Carme any rere any."
    testId={HOME.patrocinadorsSection}
  >
    <div className="bg-[#0E1410] text-[#F7F1E8] p-8 md:p-10 mb-10 grid md:grid-cols-[1fr_1.4fr] gap-6 items-center">
      <img src={IMAGES.cccp} alt="CCCP" className="w-full max-w-[320px]" />
      <div>
        <div className="font-display uppercase tracking-widest text-xs text-[#F2B23A]">Lliga oficial</div>
        <div className="font-black text-3xl mt-2">Circuit Curses Caminades Penedès 2026</div>
        <p className="text-[#F7F1E8]/70 mt-3">
          Aquest any formem part del <strong>CCCP</strong>, la lliga que reuneix les millors
          curses i caminades de la comarca del Penedès.
        </p>
      </div>
    </div>

    {/* Official sponsors board */}
    <div className="bg-white border border-[#15140F]/10 p-3 sm:p-5 md:p-6">
      <img
        src={IMAGES.patrocinadorsBoard}
        alt="Patrocinadors i col·laboradors del XIII Cross de Carme"
        className="w-full h-auto"
        loading="lazy"
      />
    </div>

    <div className="mt-12 bg-[#E94E1B] text-white p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
      <div>
        <div className="font-display uppercase tracking-widest text-xs text-white/80">Patrocini</div>
        <div className="font-black text-2xl md:text-3xl mt-1">Vols patrocinar el Cross de Carme?</div>
        <p className="text-white/85 mt-2 max-w-2xl text-sm md:text-base">
          Contacta amb nosaltres i forma part d'aquest gran esdeveniment esportiu.
        </p>
      </div>
      <a
        href="#contacte"
        className="px-7 py-4 bg-[#0E1410] text-white font-display uppercase tracking-wider text-sm hover:bg-black transition-colors"
      >
        Contacta'ns
      </a>
    </div>
  </Section>
);

const Info = () => (
  <section id="info" className="bg-[#F7F1E8] text-[#15140F] py-20 md:py-24 px-5 sm:px-8">
    <div className="max-w-7xl mx-auto">
      <div className="mb-10 max-w-3xl">
        <div className="flex items-center gap-3 mb-5">
          <span className="divider-tick" />
          <span className="font-display uppercase tracking-[0.28em] text-xs text-[#E94E1B]">
            Informació general
          </span>
        </div>
        <h2 className="font-black uppercase text-4xl md:text-6xl leading-[1.02]">
          Distàncies, premis i normativa
        </h2>
      </div>

      {/* Distàncies · 2 modalitats */}
      <div className="grid md:grid-cols-2 gap-4 mb-4">
        <div className="bg-[#0E1410] text-[#F7F1E8] p-7 border-l-4 border-[#E94E1B]">
          <div className="flex items-center gap-2 font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A]">
            <Route size={14} /> Cross · Competitiu
          </div>
          <div className="mt-3 font-black text-5xl tabular-nums">5,7 <span className="text-2xl">km</span></div>
          <p className="mt-2 text-sm text-[#F7F1E8]/70">
            Circuit oficial del Cross de Carme. Prova competitiva amb classificació i premis.
          </p>
        </div>
        <div className="bg-[#0E1410] text-[#F7F1E8] p-7 border-l-4 border-[#F2B23A]">
          <div className="flex items-center gap-2 font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A]">
            <Mountain size={14} /> Caminada · No competitiva
          </div>
          <div className="mt-3 font-black text-5xl tabular-nums">7,5 <span className="text-2xl">km</span></div>
          <p className="mt-2 text-sm text-[#F7F1E8]/70">
            Per senderes amb panoràmiques de Carme.
            <strong className="text-[#F2B23A]"> Cada any el circuit és diferent.</strong>
          </p>
        </div>
      </div>

      {/* Preu · Sortida */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-[#E94E1B] text-white p-7">
          <div className="font-display uppercase tracking-[0.22em] text-[10px] text-white/85">Preu inscripció</div>
          <div className="mt-3 font-black text-6xl">10€</div>
          <p className="mt-2 text-white/90 text-sm">
            Preu únic per a la cursa i la caminada.
          </p>
        </div>
        <div className="bg-white border border-[#15140F]/10 p-7">
          <div className="flex items-center gap-2 font-display uppercase tracking-[0.22em] text-[10px] text-[#E94E1B]">
            <MapPin size={14} /> Sortida i arribada
          </div>
          <div className="mt-3 font-black text-2xl md:text-3xl uppercase leading-tight">
            Piscina Municipal de Carme
          </div>
          <p className="mt-2 text-sm text-[#6B6358]">
            Carrer de la Piscina Municipal, Carme (Anoia).
          </p>
        </div>
      </div>

      {/* Premis absoluts */}
      <div className="mt-12">
        <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#E94E1B] mb-3">
          Premis econòmics · Categoria absoluta
        </div>
        <h3 className="font-black uppercase text-3xl md:text-4xl leading-tight mb-2">
          Els 3 primers absoluts
        </h3>
        <p className="text-sm md:text-base text-[#6B6358] mb-6">
          Els premis econòmics són per als 3 primers <strong>masculins</strong> i les 3 primeres <strong>femenines</strong> de la categoria absoluta.
        </p>
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { pos: "1r", prize: "150€", extra: "Trofeu + obsequis", featured: true },
            { pos: "2n", prize: "110€", extra: "Trofeu + obsequis" },
            { pos: "3r", prize: "100€", extra: "Trofeu + obsequis" },
          ].map((p) => (
            <div
              key={p.pos}
              className={`p-7 border ${
                p.featured
                  ? "bg-[#E94E1B] text-white border-[#E94E1B]"
                  : "bg-white text-[#15140F] border-[#15140F]/10"
              }`}
            >
              <div className={`font-display uppercase tracking-widest text-xs ${p.featured ? "text-white/85" : "text-[#E94E1B]"}`}>
                Posició {p.pos}
              </div>
              <div className="mt-3 font-black text-5xl tabular-nums">{p.prize}</div>
              <div className={`mt-2 font-display uppercase tracking-wider text-xs ${p.featured ? "text-white/90" : "text-[#6B6358]"}`}>
                + {p.extra}
              </div>
            </div>
          ))}
        </div>

        {/* Premi rècord */}
        <div className="mt-4 grid md:grid-cols-2 gap-4">
          <div className="bg-[#0E1410] text-[#F7F1E8] p-6 border-l-4 border-[#F2B23A]">
            <div className="flex items-center gap-3">
              <Sparkles size={28} className="text-[#F2B23A] shrink-0" />
              <div>
                <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A]">
                  Premi extra · Rècord masculí
                </div>
                <div className="mt-1 font-black text-xl md:text-2xl uppercase leading-tight">
                  +50€ per baixar de <span className="text-[#E94E1B]">3:20 min/km</span>
                </div>
              </div>
            </div>
            <p className="mt-3 text-sm text-[#F7F1E8]/70">
              50€ extra per al primer atleta masculí que baixi del ritme de <strong>3:20 min/km</strong>.
            </p>
          </div>

          <div className="bg-[#0E1410] text-[#F7F1E8] p-6 border-l-4 border-[#F2B23A]">
            <div className="flex items-center gap-3">
              <Sparkles size={28} className="text-[#F2B23A] shrink-0" />
              <div>
                <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A]">
                  Premi extra · Rècord femení
                </div>
                <div className="mt-1 font-black text-xl md:text-2xl uppercase leading-tight">
                  +50€ per baixar de <span className="text-[#E94E1B]">4:14 min/km</span>
                </div>
              </div>
            </div>
            <p className="mt-3 text-sm text-[#F7F1E8]/70">
              50€ extra per a la primera atleta femenina que baixi del ritme de <strong>4:14 min/km</strong>.
            </p>
          </div>
        </div>
      </div>

      {/* Categories */}
      <div className="mt-12">
        <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#E94E1B] mb-3">
          Trofeus per categoria
        </div>
        <h3 className="font-black uppercase text-3xl md:text-4xl leading-tight mb-2">
          Els 3 primers de cada categoria
        </h3>
        <p className="text-sm md:text-base text-[#6B6358] mb-6">
          Trofeu per als 3 primers <strong>masculins</strong> i les 3 primeres <strong>femenines</strong> de cada categoria.
        </p>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            "Sub 21",
            "Sènior · 21 a 40",
            "Màster 40",
            "Màster 50",
            "Màster 60",
            "1r Local",
          ].map((cat) => (
            <div key={cat} className="bg-[#0E1410] text-[#F7F1E8] p-5 border-l-2 border-[#F2B23A]">
              <Trophy size={20} className="text-[#F2B23A]" />
              <div className="mt-3 font-display font-bold uppercase text-sm md:text-base leading-tight">
                {cat}
              </div>
              <div className="mt-1 text-[10px] font-display uppercase tracking-widest text-[#F2B23A]/70">
                1r · 2n · 3r
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Normativa */}
      <div className="mt-12">
        <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#E94E1B] mb-3">
          Normativa
        </div>
        <h3 className="font-black uppercase text-3xl md:text-4xl leading-tight mb-6">
          Regles bàsiques
        </h3>
        <div className="grid md:grid-cols-2 gap-px bg-[#15140F]/10 border border-[#15140F]/10">
          {[
            { icon: Eye, t: "Dorsal visible obligatori", d: "Cal portar el dorsal sempre visible durant tota la cursa." },
            { icon: Droplet, t: "Pas pels avituallaments", d: "Cal seguir l'ordre i les indicacions del personal." },
            { icon: Footprints, t: "Respecte al recorregut", d: "No es pot abandonar el circuit oficial marcat." },
            { icon: Users, t: "Comportament esportiu", d: "Respecte cap a corredors, voluntaris i veïns." },
            { icon: ShieldAlert, t: "Decisions de l'organització", d: "L'organització té la decisió final en cas de conflicte." },
            { icon: Camera, t: "Drets d'imatge", d: "En inscriure't acceptes l'ús de fotos i vídeos amb finalitat promocional." },
          ].map(({ icon: Icon, t, d }) => (
            <div key={t} className="bg-white p-6 flex gap-4">
              <div className="shrink-0 w-11 h-11 bg-[#0E1410] text-[#F2B23A] flex items-center justify-center">
                <Icon size={20} />
              </div>
              <div>
                <div className="font-display uppercase tracking-wider text-sm">{t}</div>
                <p className="text-xs text-[#6B6358] mt-1.5 leading-relaxed">{d}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Photos + save the date */}
      <div className="grid md:grid-cols-2 gap-4 mt-12">
        <a
          href="https://drive.google.com/drive/u/0/folders/1PzGInSTR6iQOvq38fAfMmQbDpJ2fWAFI"
          target="_blank"
          rel="noreferrer"
          className="group bg-[#0E1410] text-[#F7F1E8] p-7 border-l-4 border-[#F2B23A] hover:bg-[#15140F] transition-colors"
          data-testid="info-photos-link"
        >
          <div className="flex items-center gap-2 font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A]">
            <Camera size={14} /> Fotografies 2026
          </div>
          <div className="mt-3 font-black text-2xl md:text-3xl uppercase leading-tight">
            Àlbum oficial de la cursa
          </div>
          <p className="mt-2 text-sm text-[#F7F1E8]/70">
            Descarrega't les fotografies oficials del Cross de Carme via Google Drive.
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-[#F2B23A] font-display uppercase tracking-wider text-xs group-hover:text-white transition-colors">
            Obrir Google Drive <ExternalLink size={14} />
          </div>
        </a>

        <div className="bg-[#F2B23A] text-[#0E1410] p-7 border-l-4 border-[#E94E1B]">
          <div className="flex items-center gap-2 font-display uppercase tracking-[0.22em] text-[10px] text-[#E94E1B]">
            <Calendar size={14} /> Save the date · XIV edició
          </div>
          <div className="mt-3 font-black text-3xl md:text-4xl uppercase leading-tight">
            Juny / Juliol 2027
          </div>
          <p className="mt-3 text-sm text-[#0E1410]/85 leading-relaxed">
            La 14a edició del Cross de Carme se celebrarà entre <strong>juny i juliol del 2027</strong>.
            Portarem novetats que anirem informant <strong>a partir del mes de març del 2027</strong>.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const Organitzadors = () => (
  <section id="organitzadors" className="bg-[#0E1410] text-[#F7F1E8] py-20 md:py-24 px-5 sm:px-8">
    <div className="max-w-7xl mx-auto">
      <div className="mb-10 max-w-3xl">
        <div className="flex items-center gap-3 mb-5">
          <span className="divider-tick" />
          <span className="font-display uppercase tracking-[0.28em] text-xs text-[#F2B23A]">
            Organització
          </span>
        </div>
        <h2 className="font-black uppercase text-4xl md:text-6xl leading-[1.02]">
          Qui hi ha darrere del Cross
        </h2>
        <p className="mt-5 text-base md:text-lg text-[#F7F1E8]/70">
          Dues entitats treballen colze amb colze per fer possible el Cross de Carme.
          Cadascuna aporta la seva especialitat.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {/* Training Club Esportiu Orpí */}
        <div className="bg-[#15140F] p-7 border-l-4 border-[#E94E1B]">
          <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A]">
            Organitza
          </div>
          <div className="mt-2 font-black text-2xl md:text-3xl uppercase leading-tight">
            Training Club Esportiu Orpí
          </div>
          <div className="mt-5 space-y-3">
            {[
              "Xarxes socials",
              "Cronometratge",
              "Animació",
              "Atletes elit",
            ].map((task) => (
              <div key={task} className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-[#E94E1B] shrink-0" />
                <span className="font-display uppercase tracking-wider text-sm">{task}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-5 border-t border-white/10 space-y-2 text-sm">
            <a href="mailto:clubesportiuorpi@hotmail.com" className="flex items-center gap-2 hover:text-[#F2B23A] transition-colors break-all">
              <Mail size={14} className="text-[#F2B23A] shrink-0" /> clubesportiuorpi@hotmail.com
            </a>
            <a href="tel:+34697377826" className="flex items-center gap-2 hover:text-[#F2B23A] transition-colors">
              <Phone size={14} className="text-[#F2B23A] shrink-0" /> 697 377 826
            </a>
          </div>
        </div>

        {/* Carme ets Tu */}
        <div className="bg-[#15140F] p-7 border-l-4 border-[#F2B23A]">
          <div className="font-display uppercase tracking-[0.22em] text-[10px] text-[#F2B23A]">
            Coorganitza
          </div>
          <div className="mt-2 font-black text-2xl md:text-3xl uppercase leading-tight">
            Carme ets Tu
          </div>
          <div className="mt-5 space-y-3">
            {[
              "Sponsors",
              "Patrocinis",
              "Voluntaris",
              "Assegurances",
            ].map((task) => (
              <div key={task} className="flex items-center gap-3">
                <div className="w-1.5 h-1.5 bg-[#F2B23A] shrink-0" />
                <span className="font-display uppercase tracking-wider text-sm">{task}</span>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-5 border-t border-white/10 space-y-2 text-sm">
            <a href="mailto:carmeetstu@gmail.com" className="flex items-center gap-2 hover:text-[#F2B23A] transition-colors break-all">
              <Mail size={14} className="text-[#F2B23A] shrink-0" /> carmeetstu@gmail.com
            </a>
            <a href="tel:+34629613108" className="flex items-center gap-2 hover:text-[#F2B23A] transition-colors">
              <Phone size={14} className="text-[#F2B23A] shrink-0" /> 629 613 108
            </a>
          </div>
        </div>
      </div>

      {/* Amb el suport de l'Ajuntament */}
      <div className="mt-4 bg-[#E94E1B] text-white p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-3 md:gap-5">
        <div className="font-display uppercase tracking-[0.22em] text-[10px] text-white/85 md:min-w-fit">
          Amb el suport de
        </div>
        <div className="font-black text-xl md:text-2xl uppercase">
          Ajuntament de Carme
        </div>
      </div>
    </div>
  </section>
);

const Contacte = () => {
  return (
    <Section
      id="contacte"
      eyebrow="Contacta"
      title="Parlem-ne"
      intro="Tens alguna pregunta o vols proposar una col·laboració? Estem aquí per ajudar-te."
      testId={HOME.contacteSection}
      dark
    >
      <div className="grid md:grid-cols-2 gap-5">
        <a href="mailto:clubesportiuorpi@hotmail.com" className="flex gap-5 items-start p-6 bg-[#15140F] border-l-4 border-[#E94E1B] hover:bg-[#1a1814] transition-colors">
          <Mail size={28} className="text-[#F2B23A] shrink-0" />
          <div className="min-w-0">
            <div className="font-display uppercase tracking-widest text-xs text-[#F2B23A]">Email</div>
            <div className="font-black text-xl mt-1 break-all">clubesportiuorpi@hotmail.com</div>
          </div>
        </a>
        <div className="flex gap-5 items-start p-6 bg-[#15140F] border-l-4 border-[#F2B23A]">
          <Phone size={28} className="text-[#F2B23A] shrink-0" />
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#F2B23A]">Telèfon</div>
            <div className="flex flex-wrap gap-4 mt-1">
              <a href="tel:+34697377826" className="font-black text-xl hover:text-[#E94E1B]">697 377 826</a>
            </div>
          </div>
        </div>
        <div className="flex gap-5 items-start p-6 bg-[#15140F] border-l-4 border-white">
          <MapPin size={28} className="text-[#F2B23A] shrink-0" />
          <div>
            <div className="font-display uppercase tracking-widest text-xs text-[#F2B23A]">Ubicació</div>
            <div className="font-black text-xl mt-1">Piscina Municipal de Carme</div>
            <div className="text-[#F7F1E8]/60 text-sm">Carme · Barcelona</div>
          </div>
        </div>

        <div className="bg-[#E94E1B] p-6">
          <div className="font-display uppercase tracking-widest text-xs text-white/80">Classificació sencera</div>
          <div className="font-black text-2xl mt-1">Vols consultar la classificació?</div>
          <p className="text-white/85 text-sm mt-2">Consulta la classificació completa del Cross de Carme a Sportmaniacs.</p>
          <a
            href="https://sportmaniacs.com/es/services/inscription/xiii-cross-de-carme-i-iv-marxa-de-carme-ets-tu"
            target="_blank"
            rel="noreferrer"
            className="mt-4 inline-flex items-center gap-2 bg-[#0E1410] text-white px-5 py-3 font-display uppercase tracking-wider text-sm hover:bg-black transition-colors"
          >
            Veure classificació sencera <ExternalLink size={14} />
          </a>
        </div>
      </div>
    </Section>
  );
};

const Footer = () => (
  <footer data-testid={HOME.footer} className="bg-[#0E1410] text-[#F7F1E8] border-t border-white/10">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-12 grid md:grid-cols-3 gap-8">
      <div>
        <Logo size="sm" onDark />
        <div className="mt-3 text-[10px] uppercase tracking-[0.22em] text-[#F2B23A]">
          4 Juliol · 2026
        </div>
        <p className="mt-4 text-sm text-[#F7F1E8]/60 max-w-sm">
          La XIII edició del Cross de Carme i la V Caminada Popular «Carme ets Tu».
        </p>
      </div>
      <div>
        <div className="font-display uppercase tracking-widest text-xs text-[#F2B23A]">Navegació</div>
        <ul className="mt-4 space-y-2 text-sm">
          <li><a href="#resultats" className="hover:text-[#E94E1B]">Resultats</a></li>
          <li><a href="#classificacio" className="hover:text-[#E94E1B]">Palmarès</a></li>
          <li><a href="#contacte" className="hover:text-[#E94E1B]">Contacte</a></li>
        </ul>
      </div>
      <div>
        <div className="font-display uppercase tracking-widest text-xs text-[#F2B23A]">Contacte</div>
        <ul className="mt-4 space-y-2 text-sm text-[#F7F1E8]/80">
          <li><a href="mailto:clubesportiuorpi@hotmail.com" className="hover:text-[#E94E1B] break-all">clubesportiuorpi@hotmail.com</a></li>
          <li><a href="tel:+34697377826" className="hover:text-[#E94E1B]">+34 697 377 826</a></li>
          <li>Piscina Municipal de Carme</li>
        </ul>
      </div>
    </div>
    <div className="border-t border-white/10 py-5 px-5 sm:px-8 text-xs text-[#F7F1E8]/40 max-w-7xl mx-auto flex flex-col md:flex-row justify-between gap-2">
      <div>© 2026 Cross de Carme · Carme ets Tu</div>
      <div>Organitzat amb passió a Carme, Anoia</div>
    </div>
  </footer>
);

export default function Home() {
  return (
    <div className="min-h-screen bg-[#F7F1E8]">
      <Navbar />
      <Hero />
      <AtletesXIV />
      <Resultats />
      <Classificacio />
      <Info />
      <Organitzadors />
      <Contacte />
      <Footer />
    </div>
  );
}
